import { Link, Stack, TextField } from '@chronosphereio/chrono-ui';
import { MarkdownVisualEditor, MarkdownVisualEditorOptions } from '@chronosphereio/shared';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { MarkdownPanelSpec } from '@/core';
import { OptionsEditorProps } from '../../../plugin-system';
import { MarkdownLinkButton } from './MarkdownLinkButton';

export type MarkdownPanelOptionsEditorProps = OptionsEditorProps<MarkdownPanelSpec>;

const MARKDOWN_GUIDE_URL = 'https://commonmark.org/help/';
const TEXT_INPUT_NUM_ROWS = 20;
const ENABLED_OPTIONS = new Set([
  MarkdownVisualEditorOptions.BOLD,
  MarkdownVisualEditorOptions.ITALIC,
  MarkdownVisualEditorOptions.CODE,
]);

export function MarkdownPanelOptionsEditor(props: MarkdownPanelOptionsEditorProps) {
  const {
    onChange,
    value: { text },
  } = props;

  const [hasFocused, setHasFocused] = useState(false);
  const setFocused = useCallback(() => setHasFocused(true), [setHasFocused]);

  const inputRef = useRef<HTMLTextAreaElement>();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ text: e.target.value });
  };

  const handleSave = useCallback(
    (markdownLink: string) => {
      if (inputRef.current) {
        if (hasFocused) {
          inputRef.current.setRangeText(markdownLink);
          onChange({ text: inputRef.current.value });
        } else {
          onChange({ text: `${inputRef.current.value} ${markdownLink}` });
        }
      }
    },
    [onChange, hasFocused]
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row-reverse" spacing={1} alignItems="center">
        <Link sx={{ textDecoration: 'none' }} href={MARKDOWN_GUIDE_URL} target="_blank" rel="noopener">
          Markdown Guide
        </Link>
        <MarkdownVisualEditor
          textAreaRef={inputRef}
          text={text}
          setText={(t) => onChange({ text: t })}
          enabledOptions={ENABLED_OPTIONS}
          otherActions={<MarkdownLinkButton onSave={handleSave} />}
        />
      </Stack>
      <TextField
        inputRef={inputRef}
        onFocus={setFocused}
        label="Text"
        // Multiline
        multiline
        rows={TEXT_INPUT_NUM_ROWS}
        // Value
        value={text}
        onChange={handleChange}
      />
    </Stack>
  );
}
